import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import {
  useGetClassTimetableQuery,
  useGetInstitutionIdentityQuery,
  useListExamGroupsQuery,
  getApiError,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

/*
  ═════════════════════════════════════════════════════════════════════════════
  THE TIMETABLE, ON THE WALL

  Why paper matters for this one
  ──────────────────────────────
  A timetable is consulted twenty times a day by people who are mid-corridor
  with a stack of exercise books. It lives on the classroom wall and in the
  staff room, not behind a login. A module that cannot be pinned up will be
  re-typed into Word by somebody, and from that moment the app's copy is the
  one that goes stale.

  Why landscape, declared here
  ────────────────────────────
  Five days across do not fit A4 portrait; the last two columns crush into
  unreadable slivers. The app's global @page is portrait because everything
  else it prints — report cards, registers — is a list. A later @page rule in
  the document overrides the stylesheet's, so this page rotates the paper for
  itself and leaves every other print route alone.

  Why black on white regardless of theme
  ──────────────────────────────────────
  Same reason as the report cards: browsers drop background graphics by
  default, so a dark grid prints as grey text with no borders at all.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function TimetablePrintPage() {
  const [params] = useSearchParams();
  const groupId = params.get('groupId') ?? '';
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');
  const navigate = useNavigate();

  const { data: school } = useGetInstitutionIdentityQuery(institutionId, { skip: !institutionId });
  const { data: groups = [] } = useListExamGroupsQuery(institutionId, { skip: !institutionId });
  const { data, isLoading, isError, error } = useGetClassTimetableQuery(
    { institutionId, groupId },
    { skip: !institutionId || !groupId },
  );

  const className = groups.find((g) => g._id === groupId)?.name ?? 'Class';

  useEffect(() => {
    const previous = document.body.style.background;
    document.body.style.background = '#fff';
    return () => {
      document.body.style.background = previous;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Spinner />
      </div>
    );
  }

  const periods = data?.periods ?? [];
  const byCell = new Map((data?.slots ?? []).map((s) => [`${s.day}:${s.periodId}`, s]));

  return (
    <div className="min-h-screen bg-[#f1f3f6] py-6 text-black">
      {/* Rotates the sheet for this route only. */}
      <style>{'@page { size: A4 landscape; margin: 10mm; }'}</style>

      <div className="no-print mx-auto mb-5 flex max-w-[1100px] items-center gap-2 px-4">
        <Button variant="secondary" size="sm" onClick={() => navigate('/timetable')}>
          <ArrowLeft size={13} className="mr-1" />
          Back to timetable
        </Button>
        <span className="text-[11.5px] text-neutral-600">
          {className} · one sheet, landscape
        </span>
        <Button variant="primary" size="sm" className="ml-auto" onClick={() => window.print()}>
          <Printer size={13} className="mr-1" />
          Print
        </Button>
      </div>

      {isError && (
        <p role="alert" className="mx-auto max-w-[1100px] px-4 text-[12px] font-medium text-red-700">
          {getApiError(error)}
        </p>
      )}

      {!isError && (
        <article className="print-sheet mx-auto w-[1100px] max-w-full bg-white p-9 shadow-sm print:w-full print:p-0 print:shadow-none">
          <header className="mb-5 flex items-center gap-3 border-b-2 border-black pb-3">
            {school?.logoUrl && <img src={school.logoUrl} alt="" className="h-12 w-12 object-contain" />}
            <div>
              <h1 className="text-[20px] font-bold uppercase tracking-wide">{className}</h1>
              <p className="mt-0.5 text-[12px] text-neutral-700">
                {school?.name ? `${school.name} · ` : ''}Weekly timetable
              </p>
            </div>
          </header>

          <table className="w-full border-collapse text-[11px]">
            <thead>
              <tr>
                <th className="w-[108px] border border-neutral-400 bg-neutral-100 px-2 py-1.5 text-left font-bold uppercase tracking-wide">
                  Period
                </th>
                {DAYS.map((d) => (
                  <th
                    key={d}
                    className="border border-neutral-400 bg-neutral-100 px-2 py-1.5 text-left font-bold uppercase tracking-wide"
                  >
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => (
                <tr key={p._id} className="print-keep">
                  <td className="border border-neutral-400 px-2 py-1.5 align-top">
                    <span className="block font-bold">{p.name}</span>
                    <span className="block text-[9.5px] tabular-nums text-neutral-600">
                      {p.startTime}–{p.endTime}
                    </span>
                  </td>

                  {/* Break and lunch run across the week — the same twenty
                      minutes for everybody, not five separate things. */}
                  {!p.isTeaching ? (
                    <td
                      colSpan={DAYS.length}
                      className="border border-neutral-400 bg-neutral-50 px-2 py-1.5 text-center text-[10px] font-semibold uppercase tracking-[0.1em] text-neutral-600"
                    >
                      {p.name}
                    </td>
                  ) : (
                    DAYS.map((_, i) => {
                      const slot = byCell.get(`${i + 1}:${p._id}`);
                      return (
                        <td key={i} className="border border-neutral-400 px-2 py-1.5 align-top">
                          {slot ? (
                            <>
                              <span className="block font-bold leading-tight">{slot.subject}</span>
                              <span className="block text-[9.5px] leading-tight text-neutral-600">
                                {slot.teacher}
                                {slot.teacher && slot.room ? ' · ' : ''}
                                {slot.room}
                              </span>
                            </>
                          ) : (
                            <span className="text-neutral-300">—</span>
                          )}
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {periods.length === 0 && (
            <p className="py-8 text-center text-[12px] text-neutral-600">
              No periods have been set, so there is nothing to print.
            </p>
          )}

          {/* Printed-on date: a timetable on a wall outlives its edits, and a
              sheet with no date gets trusted long after it stopped being
              true. */}
          <footer className="mt-4 flex items-center justify-between border-t border-neutral-300 pt-2 text-[9.5px] text-neutral-500">
            <span>
              Printed {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            <span>SafePass</span>
          </footer>
        </article>
      )}
    </div>
  );
}
